<template>
  <div class="iframe-wrapper">
    <!-- iframe은 항상 렌더링 — 스켈레톤 아래에서 로딩 대기 -->
    <iframe
      ref="aiFrame"
      :srcdoc="aiHtmlContent"
      class="seamless-iframe"
      :class="{ 'iframe-visible': !loading }"
      frameborder="0"
      scrolling="no"
      @load="onIframeLoad"
    ></iframe>

    <!-- 스켈레톤: iframe 위에 겹쳐서 fade-out -->
    <transition name="skeleton-fade">
      <div v-if="loading" class="ai-main-skeleton"></div>
    </transition>
  </div>
</template>

<script>
export default {
  props: {
    pageUrl: { type: String, default: null }
  },
  data() {
    return {
      aiHtmlContent: '',
      lastSetHeight: 0,
      loading: true
    };
  },
  async mounted() {
    try {
      const report = this.$store?.getters['report_list/selected_report'];
      const fontFamily = report?.font_family || "'Pretendard', 'Noto Sans KR', sans-serif";
      const vPad = this.$store?.getters['report_contents/table_vertical_padding'] || '0.25';
      const hPad = this.$store?.getters['report_contents/table_horizontal_padding'] || '0.75';

      const response = await fetch(this.pageUrl);
      if (!response.ok) throw new Error(`Status: ${response.status}`);
      let htmlText = await response.text();

      const viewportHeight = window.innerHeight;

      // iframe 내부에서 100vh는 iframe 자체 높이를 참조하므로
      // syncHeight()와 순환 참조가 발생한다.
      // HTML 내 모든 100vh를 실제 뷰포트 높이로 치환한다.
      htmlText = htmlText.replace(/100vh/g, `${viewportHeight}px`);

      const styleOverride = `
      <style>
        @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
        body { margin: 0; padding: 0; font-family: ${fontFamily}; font-size: 1rem; color: #111111; line-height: 1.6; letter-spacing: -0.01px; font-weight: 400; }
        h1 { font-size: 2.5rem; font-weight: 700; margin-bottom: 0.5rem; }
        h2 { font-size: 2rem; font-weight: 700; margin-bottom: 0.5rem; }
        h3 { font-size: 1.75rem; font-weight: 600; margin-bottom: 0.5rem; }
        h4 { font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem; }
        h5 { font-size: 1.25rem; font-weight: 600; color: #343434; margin-bottom: 0.5rem; }
        h6 { font-size: 1.1rem; font-weight: 600; margin-bottom: 0.5rem; }
        p { line-height: 1.6; font-size: 1rem; font-weight: 400; margin-bottom: 1rem; letter-spacing: -0.01px; color: #111111; text-align: justify; }
        strong { font-weight: 600; }
        hr { border: 0; border-top: 1px solid rgba(0,0,0,0.1); padding-bottom: 1rem; margin-top: 1rem; margin-bottom: 0; }
        img { max-width: 100%; height: auto; }
        a { color: #008C95; }
        blockquote { border-left: 3px solid #dee2e6; padding: 0.25rem 0 0.25rem 1rem; color: #6c757d; font-style: italic; margin: 0.5rem 0; }
        ul, ol { padding-inline-start: 1rem; font-weight: 400; }
        ul { list-style-type: disc; }
        ul ul { list-style-type: circle; }
        ul ul ul { list-style-type: square; }
        ul ul ul ul { list-style-type: disc; }
        ul ul ul ul ul { list-style-type: circle; }
        ul ul ul ul ul ul { list-style-type: square; }
        table { width: 100%; border-left: none; border-right: none; border-collapse: collapse; }
        table > tbody > tr { min-height: 32px; }
        table > tbody > tr > td { padding: ${vPad}rem ${hPad}rem; border-bottom: 1px solid #dedede; font-weight: 400; font-size: 0.9rem; background-color: white; }
        table > tbody > tr > th, table > thead > tr > th { padding: ${vPad}rem ${hPad}rem; border-top: 1px solid #a2a2a2; border-bottom: 1px solid #a2a2a2; font-weight: 600; background-color: #efefef; color: #202020; font-size: 0.9rem; }
        table > tbody > tr > td > p { margin-bottom: 0; font-weight: 400; }

        html { scroll-behavior: smooth; }
      </style>
    `;

      const scriptOverride = `
      <script>
        document.addEventListener('DOMContentLoaded', () => {
          /* 앵커 클릭 → 부모 프레임 스크롤 */
          document.body.addEventListener('click', (e) => {
            const link = e.target.closest('a');
            if (link && link.getAttribute('href') && link.getAttribute('href').startsWith('#')) {
              e.preventDefault();
              const targetId = link.getAttribute('href');
              if (targetId === '#') return;
              const targetElement = document.querySelector(targetId);
              if (targetElement) {
                const offsetPosition = targetElement.getBoundingClientRect().top;
                window.parent.scrollBy({ top: offsetPosition, behavior: 'smooth' });
              }
            }
          });
        });
      ${'<'}/script>
    `;

      // AI 페이지가 자체 완전한 스타일(Tailwind 등)을 가진 경우 styleOverride 생략
      // 자체 <style> 태그가 없는 페이지에만 styleOverride 적용
      const hasSelfContainedStyle = /<style[\s>]/i.test(htmlText);
      if (!hasSelfContainedStyle) {
        htmlText = htmlText.replace('</head>', styleOverride + '</head>');
      }
      this.aiHtmlContent = htmlText + scriptOverride;
    } catch (error) {
      console.error('로드 실패:', error);
    }
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleIframeMessage);
  },
  methods: {
    onIframeLoad() {
      // srcdoc=""인 초기 로드는 무시 — 실제 콘텐츠가 설정된 후에만 처리
      if (!this.aiHtmlContent) return;
      this.loading = false;
      this.initResizeObserver();
    },

    initResizeObserver() {
      const iframe = this.$refs.aiFrame;
      if (!iframe || !iframe.contentWindow) return;

      // iframe 내부 postMessage 기반 높이 동기화
      window.addEventListener('message', this.handleIframeMessage);

      // 폴백: ResizeObserver로도 감시
      try {
        const iframeBody = iframe.contentWindow.document.body;
        if (iframeBody) {
          new ResizeObserver(() => this.syncHeight()).observe(iframeBody);
        }
      } catch (e) {
        // cross-origin 접근 불가 시 무시 — postMessage로 대응
      }

      // SPA 초기 렌더링 대기: 2초간 500ms 간격으로 높이 체크
      let checkCount = 0;
      const initialCheck = setInterval(() => {
        this.syncHeight();
        checkCount++;
        if (checkCount >= 4) clearInterval(initialCheck);
      }, 500);
    },

    applyHeight(height) {
      const iframe = this.$refs.aiFrame;
      if (!iframe) return;
      const MAX_REASONABLE_HEIGHT = 100000;
      if (height <= 0 || height >= MAX_REASONABLE_HEIGHT) return;
      // 4px 미만 차이는 무시 — 피드백 루프 방지
      if (Math.abs(height - this.lastSetHeight) < 4) return;
      this.lastSetHeight = height;
      iframe.style.height = height + 'px';
    },

    handleIframeMessage(event) {
      if (event.data && event.data.type === 'iframe-height') {
        this.applyHeight(event.data.height);
      }
    },

    syncHeight() {
      const iframe = this.$refs.aiFrame;
      if (!iframe || !iframe.contentWindow) return;
      try {
        this.applyHeight(iframe.contentWindow.document.body.scrollHeight);
      } catch (e) {
        // cross-origin 접근 불가 시 무시
      }
    }
  }
};
</script>

<style scoped>
.iframe-wrapper {
  width: 100%;
  position: relative;
}

/* iframe: 처음엔 투명, 로딩 완료 후 fade-in */
.seamless-iframe {
  width: 100%;
  border: none;
  display: block;
  overflow: hidden;
  min-height: 100px;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.seamless-iframe.iframe-visible {
  opacity: 1;
}

/* 스켈레톤: iframe 위에 absolute로 겹침 */
.ai-main-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: #e9e9e9;
  z-index: 1;
}

/* 스켈레톤 fade-out 트랜지션 */
.skeleton-fade-leave-active {
  transition: opacity 0.6s ease-in-out;
}

.skeleton-fade-leave-to {
  opacity: 0;
}
</style>
